import React from 'react';
import styled from 'styled-components';
import { isNot } from 'styled-is';
import colors from '@honeybook/hbui/dist/styles/colors';
import Pencil16 from '@honeybook/icons/dist/icons/Pencil16';
import Trash16 from '@honeybook/icons/dist/icons/Trash16';
import { IconProps } from '@honeybook/icons/dist/types/Icon.interface';
import { useFlowThumbnail } from '~/common/hooks/useFlowThumbnail';
import FlowThumbnail from '~/components/AngularEntries/FlowThumbnail';
import { IconThumbnailContainer } from '~/components/Builder/Configure/SetThumbnail/IconThumbnailContainer';
import { IImage } from '~/types/model.types';
import { opacityHex } from '@honeybook/hbui/dist/utils/color.utils';
import { TSize } from '@honeybook/hbui/dist/tokens/types';
import Image16 from '@honeybook/icons/dist/icons/Image16';

interface Props {
    className?: string;
    image: IImage;
    icon?: (props: IconProps) => JSX.Element;
    onDelete: () => void;
    setImage: (image: IImage) => void;
    onClickEdit?: () => void;
    customModalCssClass?: string;
    modalTitle?: string;
    aspectRatio: number;
    defaultIconSize?: 16 | 42;
    brandColor?: string;
    iconColor?: string;
    backgroundColor?: string;
    size?: Extract<TSize, 'sm' | 'md'>;
}

const ImageUpload = ({
    className,
    image,
    onDelete: deleteThumbnail,
    setImage: setThumbnailImage,
    onClickEdit: onClickEditThumbnail,
    customModalCssClass,
    modalTitle,
    aspectRatio,
    defaultIconSize = 16,
    size = 'sm',
    brandColor,
    icon = Image16,
    iconColor = colors.neutral600,
    backgroundColor = colors.neutral200
}: Props) => {
    const { openThumbnailModal } = useFlowThumbnail({
        customModalCssClass,
        modalTitle,
        thumbnailImage: image,
        onSet: setThumbnailImage,
        onClickEditThumbnail,
        aspectRatio
    });

    return (
        <Container
            className={className}
            $aspectRatio={aspectRatio}
            $hasImage={!!image}
            $size={size}
        >
            <FlowThumbnail
                defaultIconSize={defaultIconSize}
                thumbnailImage={image}
                brandColor={brandColor}
                iconColor={iconColor}
                backgroundColor={backgroundColor}
                aspectRatio={aspectRatio}
                icon={icon}
            />
            <HoverContainer>
                {!!image && !!deleteThumbnail && (
                    <IconThumbnailContainer
                        onClick={deleteThumbnail}
                        toolTipText="Remove image"
                        iconType="trash"
                        isThumbnailExist={!!image}
                        target={<Trash16 color={colors.white} />}
                    />
                )}

                <IconThumbnailContainer
                    onClick={openThumbnailModal}
                    iconType="edit"
                    toolTipText="Edit or replace image"
                    isThumbnailExist={!!image}
                    target={<Pencil16 color={colors.white} />}
                />
            </HoverContainer>
        </Container>
    );
};

export default ImageUpload;

// -------------------------- Styles -------------------------- //

const SIZES = {
    sm: 60,
    md: 68
} as const satisfies Record<Props['size'], number>;

const Container = styled.div<{
    $size: Props['size'];
    $aspectRatio: number;
    $hasImage: boolean;
}>`
    width: fit-content;
    height: ${({ $size }) => SIZES[$size]}px;
    aspect-ratio: ${({ $aspectRatio }) => $aspectRatio};
    cursor: pointer;
    position: relative;
    border-radius: 4px;
    ${isNot('$hasImage')`
        > :first-child i {
            transition: opacity 250ms ease-in-out;
            opacity: 1;
        }
        &:hover > :first-child i {
            opacity: 0;
        }
    `}
`;

const HoverContainer = styled.div`
    position: absolute;
    inset: 0;
    opacity: 0;
    transition: opacity 250ms ease-in-out, background-color 250ms ease-in-out;
    display: flex;
    align-items: center;
    border-radius: inherit;

    ${Container}:hover & {
        background-color: ${opacityHex(colors.black, 30)};
        opacity: 1;
    }
`;
